import type { AxiosRequestConfig } from 'axios';
import z from 'zod';
import { api } from '../../axios.instance';
import { responseContract } from '../../lib';

/** The data dimension the Revenue Analysis chart splits its months by (ST #175). */
export const REVENUE_ANALYSIS_DIMENSIONS = [
  'payerType',
  'levelsOfCare',
  'medicaidInsurer',
] as const;

export type RevenueAnalysisDimension =
  (typeof REVENUE_ANALYSIS_DIMENSIONS)[number];

const _GetRevenueAnalysisRequestDtoSchema = z.object({
  dimension: z.enum(REVENUE_ANALYSIS_DIMENSIONS),
  /** Last month of the window, YYYY-MM. Omitted = newest month with revenue. */
  endMonth: z.string().optional(),
  facilitiesIds: z.array(z.number()).optional(),
});

export const revenueAnalysisWindowSchema = z.object({
  from: z.string(),
  to: z.string(),
  /** Every month in the window, oldest first. Up to 12; fewer when data is short. */
  months: z.array(z.string()),
});

/** Bounds for the chart's own pager — outside these there is nothing to show. */
export const revenueAnalysisAvailableRangeSchema = z.object({
  earliest: z.string().nullable(),
  latest: z.string().nullable(),
});

const revenueAnalysisPointSchema = z.object({
  month: z.string(),
  revenueUsd: z.number(),
  /** Distinct residents billed under this series that month — the per-resident denominator. */
  residents: z.number(),
});

const revenueAnalysisSeriesSchema = z.object({
  key: z.string(),
  label: z.string(),
  points: z.array(revenueAnalysisPointSchema),
});

const GetRevenueAnalysisResponseDtoSchema = z.object({
  window: revenueAnalysisWindowSchema,
  availableRange: revenueAnalysisAvailableRangeSchema,
  /**
   * Only series with revenue somewhere in the window. Both the chart series and
   * the KPI card for an empty series are dropped together, upstream.
   */
  series: z.array(revenueAnalysisSeriesSchema),
  /**
   * Average residents physically in the building each month, aligned to `window.months` — the
   * same building-wide count the Forecasted tab divides by. This is what the "Avg Revenue per
   * Resident" headline should use on every dimension except Medicaid Insurer; dividing by
   * residents summed across this dimension's own series double-counts anyone billed under more
   * than one series that month (Anna, ST #175 comment 349).
   */
  monthlyAvgResidents: z.array(z.number()),
});

export type GetRevenueAnalysisRequestDto = z.infer<
  typeof _GetRevenueAnalysisRequestDtoSchema
>;

export type GetRevenueAnalysisResponseDto = z.infer<
  typeof GetRevenueAnalysisResponseDtoSchema
>;

export type RevenueAnalysisSeries = z.infer<typeof revenueAnalysisSeriesSchema>;

export type RevenueAnalysisPoint = z.infer<typeof revenueAnalysisPointSchema>;

export type RevenueAnalysisWindow = z.infer<typeof revenueAnalysisWindowSchema>;

export type RevenueAnalysisAvailableRange = z.infer<
  typeof revenueAnalysisAvailableRangeSchema
>;

export const getRevenueAnalysis = (
  params: GetRevenueAnalysisRequestDto,
  config?: AxiosRequestConfig<GetRevenueAnalysisRequestDto>
) =>
  api
    .get('facilities/revenue-analysis', { params, ...config })
    .then(responseContract(GetRevenueAnalysisResponseDtoSchema));
